import React from "react";
import BaseField from "./BaseField";

export default function MediaField({
  field,
  value,
  setValue,
  renderInlineChildren,
  openViewer,
  previewMode = false,
}) {
  const required = !!field.required;
  const label = field.label || "Media";
  const allowMultiple = field.config?.allowMultiple !== false;

  const getFiles = () => (Array.isArray(value) ? value : (value && Array.isArray(value.files) ? value.files : []));

  const setFiles = (filesArr) => {
    // Preserve existing note if present
    if (value && typeof value === 'object' && !Array.isArray(value)) {
      setValue({ ...(value || {}), files: filesArr });
    } else {
      // if allowMultiple prefer array, otherwise store single object
      if (allowMultiple) setValue(filesArr);
      else setValue(filesArr[0] || null);
    }
  };

  const handleFileUpload = (e) => {
    const uploadedFiles = Array.from(e.target.files || []);
    if (uploadedFiles.length === 0) return;

    const newFiles = uploadedFiles.map((file) => ({
      file,
      name: file.name,
      type: file.type,
      size: file.size,
      url: URL.createObjectURL(file),
    }));

    const existing = getFiles();
    if (allowMultiple) {
      setFiles([...existing, ...newFiles]);
    } else {
      setFiles([newFiles[0]]);
    }
  };

  // Notes handling (field-level note)
  const [notesOpen, setNotesOpen] = React.useState(false);
  const [draftNote, setDraftNote] = React.useState(value && value.note ? value.note : "");

  const saveNote = () => {
    if (value && typeof value === 'object' && !Array.isArray(value)) {
      setValue({ ...(value || {}), note: draftNote });
    } else {
      const files = getFiles();
      setValue({ files, note: draftNote });
    }
    setNotesOpen(false);
  };

  const clearNote = () => {
    setDraftNote("");
  };

  // Render differently in preview mode (FormBuilder) vs runtime
  if (previewMode) {
    // Debug: confirm preview-mode branch is used in browser
    // eslint-disable-next-line no-console
    console.log('MediaField: rendering in PREVIEW mode for field', field.id);
    return (
      <BaseField
        field={field}
        value={value}
        setValue={setValue}
        openViewer={openViewer}
        renderInlineChildren={renderInlineChildren}
        previewMode={previewMode}
      >
        <h3 className="text-gray-800 dark:text-gray-100 text-lg font-bold mb-3 text-center">
          {label} {required && <span className="text-orange-500">*</span>}
        </h3>

        <div className="flex items-center gap-3 justify-center">
          <button type="button" className="px-3 py-1 bg-orange-200 text-orange-800 rounded text-sm cursor-default" disabled>
            Upload Evidence
          </button>
          <button type="button" className="px-3 py-1 bg-gray-100 rounded text-sm cursor-default" disabled>
            {value && (value.note || (value?.files && value.files.length)) ? 'Edit notes' : 'Add notes'}
          </button>
        </div>

        {(field.config?.attachments || field.attachments || []).length > 0 && (
          <div className="mt-3 flex gap-2 flex-wrap justify-center">
            {(field.config?.attachments || field.attachments || []).map((a, i) => (
              <img key={`attach-${i}`} src={a} alt={`attachment-${i}`} className="w-14 h-14 object-cover rounded border border-gray-300 shadow-sm" />
            ))}
          </div>
        )}

        {getFiles().length > 0 && (
          <div className="mt-3 flex gap-2 flex-wrap justify-center">
            {getFiles().map((file, idx) => (
              <div key={idx} className="relative">
                {file.type.startsWith("image/") && (
                  <img src={file.url} alt={file.name} className="w-16 h-16 object-cover rounded border border-gray-300" />
                )}
                {file.type.startsWith("video/") && (
                  <video src={file.url} className="w-16 h-16 object-cover rounded border border-gray-300" />
                )}
              </div>
            ))}
          </div>
        )}
      </BaseField>
    );
  }

  return (
    // Debug: confirm runtime branch is used in browser
    // eslint-disable-next-line no-console
    console.log('MediaField: rendering in RUNTIME mode for field', field.id),
    <BaseField
      field={field}
      value={value}
      setValue={setValue}
      openViewer={openViewer}
      renderInlineChildren={renderInlineChildren}
      previewMode={previewMode}
    >
      <h3 className="text-gray-800 dark:text-gray-100 text-lg font-bold mb-3 text-center">
        {label} {required && <span className="text-orange-500">*</span>}
      </h3>

      <div className="flex items-center gap-3">
        <label className="inline-flex items-center">
          <input type="file" onChange={handleFileUpload} multiple={allowMultiple} accept="image/*,video/*" className="hidden" />
          <button type="button" className="px-3 py-1 bg-orange-500 text-white rounded text-sm">Upload Evidence</button>
        </label>

        <button type="button" onClick={() => setNotesOpen((s) => !s)} className="px-3 py-1 bg-gray-100 rounded text-sm">{draftNote ? 'Edit notes' : 'Add notes'}</button>
      </div>

      {notesOpen && (
        <div className="mt-2 p-2 border border-gray-200 rounded bg-white">
          <textarea value={draftNote} onChange={(e) => setDraftNote(e.target.value)} placeholder="Add any notes..." className="w-full p-2 border rounded" />
          <div className="flex gap-2 justify-end mt-2">
            <button type="button" onClick={clearNote} className="px-3 py-1 border rounded">Clear</button>
            <button type="button" onClick={saveNote} className="px-3 py-1 bg-rose-600 text-white rounded">Save</button>
          </div>
        </div>
      )}

      {(field.config?.attachments || field.attachments || []).length > 0 && (
        <div className="mt-2 mb-2 flex gap-2 flex-wrap">
          {(field.config?.attachments || field.attachments || []).map((a, i) => (
            <div key={`attach-${i}`} className="cursor-pointer" onClick={() => openViewer?.(a)}>
              <img src={a} alt={`attachment-${i}`} className="w-20 h-20 object-cover rounded border border-gray-300" />
            </div>
          ))}
        </div>
      )}

      {getFiles().length > 0 && (
        <div className="mt-3 flex gap-2 flex-wrap">
          {getFiles().map((file, idx) => (
            <div
              key={idx}
              className="relative group"
            >
              {file.type.startsWith("image/") && (
                <img
                  src={file.url}
                  alt={file.name}
                  onClick={() => openViewer?.(file.url)}
                  className="w-20 h-20 object-cover rounded border border-gray-300 cursor-pointer hover:shadow-md transition-shadow"
                />
              )}
              {file.type.startsWith("video/") && (
                <video
                  src={file.url}
                  onClick={() => openViewer?.(file.url)}
                  className="w-20 h-20 object-cover rounded border border-gray-300 cursor-pointer"
                />
              )}
              <button
                type="button"
                onClick={() => {
                  const cur = getFiles();
                  const next = cur.filter((_, i) => i !== idx);
                  setFiles(next);
                }}
                className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full w-6 h-6 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity text-sm"
              >
                ✕
              </button>
            </div>
          ))}
        </div>
      )}
    </BaseField>
  );
}
